import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import logoEn from "@/assets/image/pdf/logo_en_header.webp";
import logoZh from "@/assets/image/pdf/logo_zh_white_header.webp";
import { getLangInfo, withLangPrefix } from "@/i18n";

interface NavItem {
  label: string;
  path: string;
}

interface ServiceGroup {
  title: string;
  items: NavItem[];
}

const primaryNavPaths = ["/", "/consulting", "/contact"];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { lang, normalizedPath } = getLangInfo(location.pathname);
  const isZh = lang === "zh-CN";

  const primaryNavItems = useMemo<NavItem[]>(() => {
    const labels = [t("layout.nav.home"), t("layout.nav.consulting"), t("layout.nav.contact")];
    return primaryNavPaths.map((path, i) => ({ path, label: labels[i] }));
  }, [t]);

  const serviceGroups = t("layout.serviceGroups", { returnObjects: true }) as ServiceGroup[];
  const serviceItems = serviceGroups.flatMap((group) => group.items);
  const isServiceActive = serviceItems.some((item) => item.path === normalizedPath);
  const logoSrc = isZh ? logoZh : logoEn;

  useEffect(() => {
    const onScroll = () => {
      const nextValue = window.scrollY > 18;
      setHeaderScrolled((current) => (current === nextValue ? current : nextValue));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const targetLang = isZh ? "zh-CN" : "en";
    if (i18n.resolvedLanguage !== targetLang) {
      void i18n.changeLanguage(targetLang);
    }
  }, [i18n, isZh]);

  const withLang = (path: string) => withLangPrefix(path, lang);
  const handleSwitchLang = (target: "zh-CN" | "en") => {
    const targetPath = withLangPrefix(normalizedPath, target);
    navigate(`${targetPath}${location.search}${location.hash}`, { replace: true });
    void i18n.changeLanguage(target);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead />
      <header className={`fixed top-0 left-0 right-0 z-50 header-shell ${headerScrolled ? "header-shell-scrolled" : ""}`}>
        <div className="container mx-auto flex items-center justify-between h-20 px-4">
          <Link to={withLang("/")} className="flex items-center gap-2">
            <img
              src={logoSrc}
              alt={t("layout.brand.name")}
              width={488}
              height={112}
              fetchpriority="high"
              decoding="async"
              className="h-12 sm:h-14 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {primaryNavItems.slice(0, 1).map((item) => (
              <Link
                key={item.path}
                to={withLang(item.path)}
                className={`px-3 py-2 text-sm rounded-md transition-colors ${
                  normalizedPath === item.path
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                {item.label}
              </Link>
            ))}

            <div className="relative group">
              <button
                type="button"
                className={`px-3 py-2 text-sm rounded-md transition-colors inline-flex items-center gap-1 ${
                  isServiceActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                {t("layout.nav.systems")}
                <ChevronDown className="w-4 h-4" />
              </button>

              <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 absolute top-full left-0 mt-2 w-[560px] rounded-xl border border-border bg-card/95 backdrop-blur p-4 shadow-xl">
                <div className="grid grid-cols-3 gap-4">
                  {serviceGroups.map((group) => (
                    <div key={group.title}>
                      <p className="text-xs font-semibold text-muted-foreground mb-2">{group.title}</p>
                      <div className="flex flex-col gap-1">
                        {group.items.map((item) => (
                          <Link
                            key={item.path}
                            to={withLang(item.path)}
                            className={`px-2 py-1.5 text-sm rounded-md transition-colors ${
                              normalizedPath === item.path
                                ? "text-primary bg-primary/10"
                                : "text-foreground/85 hover:text-foreground hover:bg-secondary"
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {primaryNavItems.slice(1).map((item) => (
              <Link
                key={item.path}
                to={withLang(item.path)}
                className={`px-3 py-2 text-sm rounded-md transition-colors ${
                  normalizedPath === item.path
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-1">
              <button
                type="button"
                onClick={() => handleSwitchLang("zh-CN")}
                className={`px-2.5 py-1 text-xs rounded ${isZh ? "bg-primary/20 text-primary" : "text-muted-foreground"}`}
              >
                {t("layout.lang.zh")}
              </button>
              <button
                type="button"
                onClick={() => handleSwitchLang("en")}
                className={`px-2.5 py-1 text-xs rounded ${!isZh ? "bg-primary/20 text-primary" : "text-muted-foreground"}`}
              >
                {t("layout.lang.en")}
              </button>
            </div>
            <Link to={withLang("/contact")} className="gradient-gold text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity">
              {t("layout.nav.cta")}
            </Link>
          </div>

          <button
            type="button"
            aria-label={mobileOpen ? t("layout.mobileNav.close", "Close menu") : t("layout.mobileNav.open", "Open menu")}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-foreground p-2"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen ? (
          <nav id="mobile-navigation" className="lg:hidden overflow-hidden glass-card border-t border-border">
            <div className="px-4 py-4 flex flex-col gap-1">
              <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-1 mb-2 w-fit">
                <button
                  type="button"
                  onClick={() => handleSwitchLang("zh-CN")}
                  className={`px-2.5 py-1 text-xs rounded ${isZh ? "bg-primary/20 text-primary" : "text-muted-foreground"}`}
                >
                  {t("layout.lang.zh")}
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchLang("en")}
                  className={`px-2.5 py-1 text-xs rounded ${!isZh ? "bg-primary/20 text-primary" : "text-muted-foreground"}`}
                >
                  {t("layout.lang.en")}
                </button>
              </div>

              {primaryNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={withLang(item.path)}
                  onClick={() => setMobileOpen(false)}
                  className={`px-3 py-2 text-sm rounded-md transition-colors ${
                    normalizedPath === item.path
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.label}
                </Link>
              ))}

              <div className="mt-2 border border-border rounded-lg p-3">
                <p className="text-xs font-semibold text-muted-foreground mb-2">{t("layout.nav.systems")}</p>
                <div className="space-y-3">
                  {serviceGroups.map((group) => (
                    <div key={group.title}>
                      <p className="text-xs text-muted-foreground mb-1">{group.title}</p>
                      <div className="flex flex-wrap gap-1">
                        {group.items.map((item) => (
                          <Link
                            key={item.path}
                            to={withLang(item.path)}
                            onClick={() => setMobileOpen(false)}
                            className={`px-2.5 py-1.5 text-xs rounded-md transition-colors ${
                              normalizedPath === item.path
                                ? "text-primary bg-primary/10"
                                : "text-foreground/80 bg-secondary/50"
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Link to={withLang("/contact")} onClick={() => setMobileOpen(false)} className="gradient-gold text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold text-center mt-2">
                {t("layout.nav.cta")}
              </Link>
            </div>
          </nav>
        ) : null}
      </header>

      <main className="pt-20">
        <Outlet />
      </main>

      <footer className="border-t border-border bg-card">
        <div className="container mx-auto px-4 py-12">
          <p className="text-center text-xs text-muted-foreground">{t("layout.footer", { year: new Date().getFullYear() })}</p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
